import { changeMetaThemeColor, onDocumentLoaded, setHeaderMenuStyle } from "@theme/utilities";
import { Component } from "@theme/component";
import { ensureGsap, prefersReducedMotion as prefersReducedMotion$1 } from "@theme/vp-motion";
import { getIntersectionRoot, getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from "@theme/scroll-container";
import { initBrandDropdownNavigation } from "@theme/brand-dropdown-navigation";
//#region assets/header.ts
/**
* @typedef {Object} HeaderComponentRefs
* @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
* @property {HTMLElement} headerMenu - The header menu element
* @property {HTMLElement} headerRowTop - The header top row element
*/
/**
* @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
*/
/**
* A custom element that manages the site header.
*
* @extends {Component<HeaderComponentRefs>}
*/
var HeaderComponent = class HeaderComponent extends Component {
	static TOP_EPSILON = 1;
	/**
	* Minimum sustained travel (px) before `data-scroll-direction` flips.
	* Must exceed the scroll shift the announcement bar induces when it
	* collapses, otherwise that shift retriggers the toggle in a loop.
	*/
	static DIRECTION_HYSTERESIS = 64;
	requiredRefs = ["headerRowTop"];
	/**
	* Width of window when header drawer was hidden
	* @type {number | null}
	*/
	#menuDrawerHiddenWidth = null;
	/**
	* An intersection observer for monitoring sticky header position
	* @type {IntersectionObserver | null}
	*/
	#intersectionObserver = null;
	/** @type {EventTarget | null} */
	#scrollContainer = null;
	/**
	* Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
	* @type {boolean}
	*/
	#offscreen = false;
	/**
	* The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
	* @type {number}
	*/
	#lastScrollTop = 0;
	/**
	* Accumulated scroll travel since the last committed direction change.
	* @type {number}
	*/
	#directionTravel = 0;
	/**
	* A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
	* @type {number | null}
	*/
	#timeout = null;
	/**
	* RAF ID for scroll handler throttling
	* @type {number | null}
	*/
	#scrollRafId = null;
	/**
	* Keeps the global `--header-height` custom property up to date,
	* which other theme components can then consume
	*/
	#resizeObserver = new ResizeObserver(([entry]) => {
		if (!entry || !entry.borderBoxSize[0]) return;
		const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
		document.body.style.setProperty("--header-height", `${roundedHeaderHeight}px`);
		if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) this.#updateMenuVisibility(false);
	});
	/**
	* Observes the header while scrolling the viewport to track when its actively sticky
	* @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
	*/
	#observeStickyPosition = (alwaysSticky = true) => {
		if (this.#intersectionObserver) return;
		const config = {
			threshold: alwaysSticky ? 1 : 0,
			root: getIntersectionRoot()
		};
		this.#intersectionObserver = new IntersectionObserver(([entry]) => {
			if (!entry) return;
			const { isIntersecting } = entry;
			if (alwaysSticky) {
				this.dataset.stickyState = isIntersecting ? "inactive" : "active";
				if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
			} else this.#offscreen = !isIntersecting || this.dataset.stickyState === "active";
		}, config);
		this.#intersectionObserver.observe(this);
	};
	/**
	* Handles the overflow minimum event from the header menu
	* @param {OverflowMinimumEvent} event
	*/
	#handleOverflowMinimum = (event) => {
		this.#updateMenuVisibility(event.detail.minimumReached);
	};
	/**
	* Updates the visibility of the menu and drawer
	* @param {boolean} hideMenu - Whether to hide the menu and show the drawer
	*/
	#updateMenuVisibility(hideMenu) {
		if (hideMenu) this.#menuDrawerHiddenWidth = window.innerWidth;
		else {
			this.#menuDrawerHiddenWidth = null;
			const overflowList = this.querySelector("overflow-list");
			if (overflowList) overflowList.removeAttribute("minimum-reached");
		}
		setHeaderMenuStyle();
	}
	/**
	* Rebinds the scroll listener and IntersectionObserver when the viewport
	* crosses the squeeze breakpoint (990px). The scroll container switches
	* between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
	* so cached bindings from initialization become stale after a resize.
	*/
	#handleBreakpointChange = () => {
		const stickyMode = this.getAttribute("sticky");
		if (!stickyMode) return;
		if (this.#scrollContainer) {
			this.#scrollContainer.removeEventListener("scroll", this.#handleWindowScroll);
			this.#scrollContainer = getScrollEventTarget();
			this.#scrollContainer.addEventListener("scroll", this.#handleWindowScroll);
		}
		this.#intersectionObserver?.disconnect();
		this.#intersectionObserver = null;
		this.#observeStickyPosition(stickyMode === "always");
	};
	#handleWindowScroll = () => {
		if (this.#scrollRafId !== null) return;
		this.#scrollRafId = requestAnimationFrame(() => {
			this.#scrollRafId = null;
			this.#updateScrollState();
		});
	};
	#updateScrollState = () => {
		const stickyMode = this.getAttribute("sticky");
		if (!this.#offscreen && stickyMode !== "always") return;
		const scrollTop = getScrollTop();
		const scrollDelta = scrollTop - this.#lastScrollTop;
		const isAtTop = this.getBoundingClientRect().top >= -HeaderComponent.TOP_EPSILON;
		if (scrollDelta !== 0 && Math.sign(scrollDelta) !== Math.sign(this.#directionTravel)) this.#directionTravel = 0;
		this.#directionTravel += scrollDelta;
		const directionSettled = Math.abs(this.#directionTravel) >= HeaderComponent.DIRECTION_HYSTERESIS;
		const settledScrollingUp = this.#directionTravel < 0;
		if (this.#timeout) {
			clearTimeout(this.#timeout);
			this.#timeout = null;
		}
		if (stickyMode === "always") {
			if (isAtTop) {
				this.dataset.scrollDirection = "none";
				this.#directionTravel = 0;
			} else if (directionSettled) {
				this.dataset.scrollDirection = settledScrollingUp ? "up" : "down";
				this.#directionTravel = 0;
			}
			this.#lastScrollTop = scrollTop;
			return;
		}
		if (!directionSettled) {
			this.#lastScrollTop = scrollTop;
			return;
		}
		this.#directionTravel = 0;
		if (settledScrollingUp) {
			if (isAtTop) {
				this.#offscreen = false;
				this.dataset.stickyState = "inactive";
				this.dataset.scrollDirection = "none";
			} else {
				this.dataset.stickyState = "active";
				this.dataset.scrollDirection = "up";
			}
		} else if (this.dataset.stickyState === "active") {
			this.dataset.scrollDirection = "none";
			this.dataset.stickyState = "idle";
		} else {
			this.dataset.scrollDirection = "none";
			this.dataset.stickyState = "idle";
		}
		this.#lastScrollTop = scrollTop;
	};
	/**
	* Absorbs a scroll shift caused by our own chrome resizing (e.g. the
	* announcement bar collapsing) so it is not mistaken for user scrolling.
	*/
	resyncScrollBaseline() {
		this.#lastScrollTop = getScrollTop();
		this.#directionTravel = 0;
	}
	connectedCallback() {
		super.connectedCallback();
		this.#resizeObserver.observe(this);
		this.addEventListener("overflowMinimum", this.#handleOverflowMinimum);
		const stickyMode = this.getAttribute("sticky");
		if (stickyMode) {
			this.#observeStickyPosition(stickyMode === "always");
			if (stickyMode === "scroll-up" || stickyMode === "always") {
				this.#scrollContainer = getScrollEventTarget();
				this.#scrollContainer.addEventListener("scroll", this.#handleWindowScroll);
			}
			scrollContainerMediaQuery.addEventListener("change", this.#handleBreakpointChange);
		}
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.#resizeObserver.disconnect();
		this.#intersectionObserver?.disconnect();
		this.removeEventListener("overflowMinimum", this.#handleOverflowMinimum);
		scrollContainerMediaQuery.removeEventListener("change", this.#handleBreakpointChange);
		this.#scrollContainer?.removeEventListener("scroll", this.#handleWindowScroll);
		this.#scrollContainer = null;
		if (this.#scrollRafId !== null) {
			cancelAnimationFrame(this.#scrollRafId);
			this.#scrollRafId = null;
		}
		document.body.style.setProperty("--header-height", "0px");
	}
};
if (!customElements.get("header-component")) customElements.define("header-component", HeaderComponent);
onDocumentLoaded(async () => {
	const header = document.querySelector("header-component");
	const headerGroup = document.querySelector("#header-group");
	const announcementBars = document.querySelectorAll("#header-group .announcement-bar[data-topbar-announcement-bar]");
	if (header && announcementBars.length > 0) {
		const applyTopbarVisibility = () => {
			const hide = header.dataset.scrollDirection === "down";
			announcementBars.forEach((bar) => {
				bar.dataset.hiddenOnScroll = hide ? "true" : "false";
			});
		};
		applyTopbarVisibility();
		new MutationObserver(() => {
			applyTopbarVisibility();
		}).observe(header, {
			attributes: true,
			attributeFilter: ["data-scroll-direction", "data-sticky-state"]
		});
	}
	if (headerGroup) {
		const resizeObserver = new ResizeObserver((entries) => {
			const headerGroupHeight = entries.reduce((totalHeight, entry) => {
				if (entry.target !== header || header.hasAttribute("transparent") && header.parentElement?.nextElementSibling) return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
				return totalHeight;
			}, 0);
			const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
			document.body.style.setProperty("--header-group-height", `${roundedHeaderGroupHeight}px`);
			if (header instanceof HTMLElement && "resyncScrollBaseline" in header)
 /** @type {any} */ header.resyncScrollBaseline();
		});
		if (header instanceof HTMLElement) resizeObserver.observe(header);
		const children = headerGroup.children;
		for (let i = 0; i < children.length; i++) {
			const element = children[i];
			if (element instanceof HTMLElement) resizeObserver.observe(element);
		}
		new MutationObserver((mutations) => {
			for (const mutation of mutations) if (mutation.type === "childList") {
				const children = headerGroup.children;
				for (let i = 0; i < children.length; i++) {
					const element = children[i];
					if (element instanceof HTMLElement) resizeObserver.observe(element);
				}
			}
		}).observe(headerGroup, { childList: true });
	}
	const vpGsap = await ensureGsap();
	const vpHeaderRoot = document.querySelector("header-component");
	const brandDropdownNavigation = vpHeaderRoot instanceof HTMLElement ? initBrandDropdownNavigation({
		root: vpHeaderRoot,
		document,
		hoverMedia: window.matchMedia("(any-hover: hover) and (any-pointer: fine)"),
		closeDelay: 150,
		gsap: vpGsap
	}) : null;
	const closeBrandDropdowns = () => brandDropdownNavigation?.close();
	window.vpCloseBrandDropdowns = closeBrandDropdowns;
	const menuToggle = document.querySelector("[data-vp-menu-toggle]");
	const fullscreenMenu = document.querySelector("[data-vp-fullscreen-menu]");
	const menuClose = document.querySelector("[data-vp-menu-close]");
	const menuItems = Array.from(document.querySelectorAll("[data-vp-menu-item]"));
	const columnsContainer = document.querySelector("[data-vp-menu-columns]");
	const menuContentCol = document.querySelector("[data-vp-menu-content-col]");
	const campaignTitle = document.querySelector("[data-vp-menu-campaign-title]");
	const campaignCopy = document.querySelector("[data-vp-menu-campaign-copy]");
	const campaignCta = document.querySelector("[data-vp-menu-campaign-cta]");
	const campaignMedia = document.querySelector("[data-vp-menu-campaign-media]");
	const menuPanels = Array.from(document.querySelectorAll("[data-vp-menu-panel]"));
	const mobileMenuMq = window.matchMedia("(max-width: 1099px)");
	const VP_PLP = "/collections/all";
	const vpFilter = (params) => {
		const query = Object.entries(params).map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join("&");
		return `${VP_PLP}?${query}`;
	};
	const vpMerk = (merk) => vpFilter({ "filter.p.m.custom.merk": merk });
	const vpType = (merk, type) => vpFilter({
		"filter.p.m.custom.merk": merk,
		"filter.p.m.custom.filter_producttype": type
	});
	const vpLifestyle = (value, merk) => merk ? vpFilter({
		"filter.p.m.custom.merk": merk,
		"filter.p.m.custom.filter_lifestyle_producten": value
	}) : vpFilter({ "filter.p.m.custom.filter_lifestyle_producten": value });
	const vpLink = (label, href) => ({
		label,
		href
	});
	const DEFAULT_PANEL_DATA = {
		Accessoires: {
			sections: [
				{
					title: "BMW",
					links: [
						vpLink("Interieur", "/collections/bmw-interieuraccessoires"),
						vpLink("Exterieur", "/collections/bmw-exterieuraccessoires"),
						vpLink("Wielen en banden", "/collections/bmw-velgen"),
						vpLink("Reizen en transport", "/collections/transportoplossingen"),
						vpLink("Onderhoud", "/collections/bmw-onderhoudsartikelen")
					]
				},
				{
					title: "MINI",
					links: [
						vpLink("Interieur", "/collections/mini-interieuraccessoires"),
						vpLink("Exterieur", "/collections/mini-exterieuraccessoires"),
						vpLink("Wielen en banden", "/collections/mini-velgen"),
						vpLink("Onderhoud", "/collections/mini-onderhoudsartikelen")
					]
				},
				{
					title: "Populair",
					links: [
						vpLink("Vloermatten", vpFilter({ "filter.p.m.custom.filter_producttype": "Automatten" })),
						vpLink("Dakdragers", vpFilter({ "filter.p.m.custom.filter_producttype": "Dakdragers" })),
						vpLink("Laadkabels", vpFilter({ "filter.p.m.custom.filter_producttype": "Laadoplossingen" })),
						vpLink("Sleutelhoezen", vpLifestyle("Sleuteletuis"))
					]
				}
			],
			campaign: {
				title: "Nieuw binnen",
				copy: "Ontdek de laatste accessoires voor jouw BMW of MINI.",
				cta: "BEKIJK NIEUW",
				href: `${VP_PLP}?sort_by=created-descending`
			}
		},
		Lifestyle: {
			sections: [
				{
					title: "Kleding",
					links: [
						vpLink("Jassen", vpLifestyle("Kleding")),
						vpLink("T-shirts", vpLifestyle("Kleding")),
						vpLink("Polo's", vpLifestyle("Kleding")),
						vpLink("Hoofddeksels", vpLifestyle("Petten"))
					]
				},
				{
					title: "Collecties",
					links: [
						vpLink("BMW Collection", vpType("BMW", "Lifestyle")),
						vpLink("MINI Collection", vpType("MINI", "Lifestyle")),
						vpLink("BMW M Collection", "/collections/m-performance-parts")
					]
				},
				{
					title: "Miniaturen",
					links: [
						vpLink("Schaalmodellen 1:18", vpLifestyle("Miniaturen")),
						vpLink("Schaalmodellen 1:43", vpLifestyle("Miniaturen")),
						vpLink("Sleutelhangers", vpLifestyle("Sleutelhangers"))
					]
				}
			],
			campaign: {
				title: "Cadeau nodig?",
				copy: "Bekijk onze cadeausets en unieke lifestyle-collecties voor elke gelegenheid.",
				cta: "BEKIJK CADEAUS",
				href: "/collections/bmw-cadeaus"
			}
		},
		Aanbiedingen: {
			sections: [
				{
					title: "BMW",
					links: [
						vpLink("Sale accessoires", `/collections/deals?filter.p.m.custom.merk=${encodeURIComponent("BMW")}`),
						vpLink("Sale lifestyle", `/collections/deals?filter.p.m.custom.merk=${encodeURIComponent("BMW")}&filter.p.m.custom.filter_producttype=${encodeURIComponent("Lifestyle")}`),
						vpLink("Outlet", "/collections/deals")
					]
				},
				{
					title: "MINI",
					links: [
						vpLink("Sale accessoires", `/collections/deals?filter.p.m.custom.merk=${encodeURIComponent("MINI")}`),
						vpLink("Sale lifestyle", `/collections/deals?filter.p.m.custom.merk=${encodeURIComponent("MINI")}&filter.p.m.custom.filter_producttype=${encodeURIComponent("Lifestyle")}`),
						vpLink("Outlet", "/collections/deals")
					]
				},
				{
					title: "Populair in sale",
					links: [
						vpLink("Vloermatten", `/collections/deals?filter.p.m.custom.filter_producttype=${encodeURIComponent("Automatten")}`),
						vpLink("Sleutelhangers", `/collections/deals?filter.p.m.custom.filter_lifestyle_producten=${encodeURIComponent("Sleutelhangers")}`),
						vpLink("T-shirts", `/collections/deals?filter.p.m.custom.filter_lifestyle_producten=${encodeURIComponent("Kleding")}`)
					]
				}
			],
			campaign: {
				title: "Tot 40% korting",
				copy: "Profiteer van tijdelijke aanbiedingen op originele BMW en MINI producten.",
				cta: "BEKIJK AANBIEDINGEN",
				href: "/collections/deals"
			}
		},
		"Over Van Poelgeest": {
			sections: [
				{
					title: "Bedrijf",
					links: [
						vpLink("Ons verhaal", "/pages/over-ons"),
						vpLink("Missie en visie", "/pages/over-ons"),
						vpLink("Duurzaamheid", "/pages/over-ons"),
						vpLink("Werken bij ons", "/pages/over-ons")
					]
				},
				{
					title: "Onze merken",
					links: [
						vpLink("BMW", vpMerk("BMW")),
						vpLink("MINI", vpMerk("MINI")),
						vpLink("BMW M", "/collections/m-performance-parts"),
						vpLink("BMW Motorrad", `${VP_PLP}?q=${encodeURIComponent("Motorrad")}`)
					]
				},
				{
					title: "Vestigingen",
					links: [
						vpLink("Alphen aan den Rijn", "/pages/contact"),
						vpLink("Gouda", "/pages/contact"),
						vpLink("Leiden", "/pages/contact"),
						vpLink("Zoetermeer", "/pages/contact")
					]
				}
			],
			campaign: {
				title: "Al meer dan 75 jaar",
				copy: "Van Poelgeest is uw officiele BMW- en MINI-partner in de regio.",
				cta: "ONS VERHAAL",
				href: "/pages/over-ons"
			}
		},
		Klantenservice: {
			sections: [
				{
					title: "Hulp & contact",
					links: [
						vpLink("Veelgestelde vragen", "/pages/veelgestelde-vragen"),
						vpLink("Contact opnemen", "/pages/contact"),
						vpLink("Terugbelverzoek", "/pages/contact")
					]
				},
				{
					title: "Bestellingen",
					links: [
						vpLink("Bestelling volgen", "/pages/bestellen-en-betalen"),
						vpLink("Retourneren", "/pages/retourneren"),
						vpLink("Ruilen", "/pages/retourneren")
					]
				},
				{
					title: "Garantie",
					links: [vpLink("Garantievoorwaarden", "/pages/algemene-voorwaarden"), vpLink("Garantieclaim indienen", "/pages/contact")]
				}
			],
			campaign: {
				title: "We helpen je graag",
				copy: "Bereikbaar op werkdagen van 09:00 tot 17:30 via telefoon, e-mail of chat.",
				cta: "NEEM CONTACT OP",
				href: "/pages/contact"
			}
		},
		Contact: {
			sections: [
				{
					title: "Bereikbaarheid",
					links: [
						vpLink("Telefoon", "/pages/contact"),
						vpLink("E-mail", "/pages/contact"),
						vpLink("Live chat", "/pages/contact"),
						vpLink("WhatsApp", "/pages/contact")
					]
				},
				{
					title: "Vestigingen",
					links: [
						vpLink("Alphen aan den Rijn", "/pages/contact"),
						vpLink("Gouda", "/pages/contact"),
						vpLink("Leiden", "/pages/contact"),
						vpLink("Zoetermeer", "/pages/contact")
					]
				},
				{
					title: "Social media",
					links: [
						vpLink("Facebook", "/pages/contact"),
						vpLink("Instagram", "/pages/contact"),
						vpLink("YouTube", "/pages/contact"),
						vpLink("LinkedIn", "/pages/contact")
					]
				}
			],
			campaign: {
				title: "Bezoek ons",
				copy: "Kom langs bij een van onze vier vestigingen in de regio Rijn Gouwe.",
				cta: "VESTIGINGEN BEKIJKEN",
				href: "/pages/contact"
			}
		}
	};
	/** @type {Record<string, { sections: Array<{ title: string, links: Array<{ label: string, href: string }> }>, campaign: { title: string, copy: string, cta: string, href?: string, image?: string }, navLabel?: string }>} */
	let PANEL_DATA = DEFAULT_PANEL_DATA;
	const megaMenuDataEl = document.getElementById("vp-mega-menu-data");
	if (megaMenuDataEl?.textContent) try {
		const parsed = JSON.parse(megaMenuDataEl.textContent);
		if (parsed?.panels && typeof parsed.panels === "object") {
			PANEL_DATA = { ...DEFAULT_PANEL_DATA };
			for (const [key, panel] of Object.entries(parsed.panels)) {
				const fallback = DEFAULT_PANEL_DATA[key];
				PANEL_DATA[key] = {
					navLabel: panel.navLabel || fallback?.navLabel,
					sections: panel.sections?.length ? panel.sections : fallback?.sections ?? [],
					campaign: {
						title: panel.campaign?.title || fallback?.campaign?.title || "",
						copy: panel.campaign?.copy || fallback?.campaign?.copy || "",
						cta: panel.campaign?.cta || fallback?.campaign?.cta || "",
						href: panel.campaign?.href || fallback?.campaign?.href || "#",
						...panel.campaign?.image ? { image: panel.campaign.image } : fallback?.campaign?.image ? { image: fallback.campaign.image } : {}
					}
				};
			}
		}
	} catch {}
	const getDefaultPanelKey = () => {
		return menuItems.find((item) => {
			const key = item.dataset.vpMenuItem || "";
			return key && PANEL_DATA[key];
		})?.dataset.vpMenuItem || menuItems[0]?.dataset.vpMenuItem || Object.keys(PANEL_DATA)[0] || "";
	};
	const showMenuPanel = (panelKey, { animate = true } = {}) => {
		const nextPanel = menuPanels.find((panel) => panel.getAttribute("data-vp-menu-panel") === panelKey);
		const currentPanel = menuPanels.find((panel) => panel.classList.contains("is-active") && !panel.classList.contains("is-leaving") && !panel.classList.contains("is-leaving-right"));
		if (!(nextPanel instanceof HTMLElement) || nextPanel === currentPanel) return;
		if (vpGsap && mobileMenuMq.matches && fullscreenMenu instanceof HTMLElement) {
			const mobileLinks = fullscreenMenu.querySelectorAll(".vp-fullscreen-menu__link");
			vpGsap.killTweensOf(mobileLinks);
			vpGsap.set(mobileLinks, { clearProps: "opacity,visibility,transform" });
		}
		const clearPanelState = () => {
			menuPanels.forEach((panel) => {
				panel.classList.toggle("is-active", panel === nextPanel);
				panel.classList.remove("is-leaving", "is-leaving-right", "is-from-left");
			});
			nextPanel.scrollTop = 0;
		};
		if (!animate || prefersReducedMotion$1() || !(currentPanel instanceof HTMLElement)) {
			clearPanelState();
			return;
		}
		if (Boolean(currentPanel.querySelector(`[data-vp-menu-back="${panelKey}"]`))) {
			nextPanel.classList.add("is-from-left");
			nextPanel.offsetWidth;
			currentPanel.classList.add("is-leaving-right");
			nextPanel.classList.add("is-active");
		} else {
			currentPanel.classList.add("is-leaving");
			nextPanel.classList.add("is-active");
		}
		window.setTimeout(clearPanelState, 320);
	};
	const resetMobileMenu = () => {
		menuPanels.forEach((panel) => {
			const isRoot = panel.getAttribute("data-vp-menu-panel") === "root";
			panel.classList.toggle("is-active", isRoot);
			panel.classList.remove("is-leaving", "is-leaving-right", "is-from-left");
		});
	};
	const lockBodyScrollForFullscreenMenu = () => {
		document.body.style.overflow = "hidden";
		document.body.style.paddingRight = "";
	};
	const unlockBodyScrollForFullscreenMenu = () => {
		document.body.style.overflow = "";
		document.body.style.paddingRight = "";
	};
	const closeFullscreenMenu = ({ animate = true, restoreScroll = true } = {}) => {
		if (!(fullscreenMenu instanceof HTMLElement) || fullscreenMenu.hidden) return;
		const finish = () => {
			fullscreenMenu.hidden = true;
			if (restoreScroll) unlockBodyScrollForFullscreenMenu();
			document.documentElement.classList.remove("vp-fullscreen-open");
			resetMobileMenu();
			if (vpGsap) {
				vpGsap.set(fullscreenMenu, { clipPath: "inset(0 0 100% 0)" });
				vpGsap.set(menuItems, { clearProps: "opacity,visibility,transform" });
			}
		};
		if (!animate || !vpGsap || prefersReducedMotion$1()) {
			finish();
			return;
		}
		vpGsap.to(fullscreenMenu, {
			clipPath: "inset(0 0 100% 0)",
			duration: .4,
			ease: "power3.inOut",
			overwrite: true,
			onComplete: finish
		});
	};
	window.vpCloseFullscreenMenu = closeFullscreenMenu;
	mobileMenuMq.addEventListener("change", () => {
		if (!mobileMenuMq.matches) closeFullscreenMenu({ animate: false });
	});
	const openFullscreenMenu = () => {
		if (!(fullscreenMenu instanceof HTMLElement)) return;
		closeBrandDropdowns();
		window.vpHeaderFlyouts?.closeAll();
		resetMobileMenu();
		fullscreenMenu.hidden = false;
		lockBodyScrollForFullscreenMenu();
		document.documentElement.classList.add("vp-fullscreen-open");
		const animateTargets = mobileMenuMq.matches ? Array.from(document.querySelectorAll("[data-vp-menu-panel=\"root\"] .vp-fullscreen-menu__link")) : menuItems;
		if (vpGsap && !prefersReducedMotion$1()) vpGsap.timeline({ overwrite: true }).fromTo(fullscreenMenu, {
			clipPath: "inset(0 0 100% 0)",
			autoAlpha: 1
		}, {
			clipPath: "inset(0 0 0% 0)",
			duration: .55,
			ease: "power3.inOut"
		}).fromTo(animateTargets, {
			autoAlpha: 0,
			y: 20
		}, {
			autoAlpha: 1,
			y: 0,
			duration: .3,
			ease: "power2.out",
			stagger: .04,
			onComplete: () => {
				vpGsap.set(animateTargets, { clearProps: "opacity,visibility,transform" });
			}
		}, "-=0.15");
		else if (vpGsap) {
			vpGsap.set(fullscreenMenu, {
				clipPath: "inset(0 0 0% 0)",
				autoAlpha: 1
			});
			vpGsap.set(animateTargets, { clearProps: "opacity,visibility,transform" });
		}
	};
	const renderMenuPanel = (activeKey) => {
		if (!(columnsContainer instanceof HTMLElement)) return;
		const panel = PANEL_DATA[activeKey] || PANEL_DATA[getDefaultPanelKey()];
		if (!panel) return;
		columnsContainer.innerHTML = panel.sections.map((section) => {
			const links = section.links.map((link) => {
				const label = typeof link === "string" ? link : link.label;
				return `<li><a href="${typeof link === "string" ? "#" : link.href}">${label}</a></li>`;
			}).join("");
			return `
          <div class="vp-fullscreen-menu__section">
            <p class="vp-fullscreen-menu__section-title">${section.title}</p>
            <ul class="vp-fullscreen-menu__links">${links}</ul>
          </div>
        `;
		}).join("");
		if (campaignTitle instanceof HTMLElement) campaignTitle.textContent = panel.campaign.title;
		if (campaignCopy instanceof HTMLElement) campaignCopy.textContent = panel.campaign.copy;
		if (campaignCta instanceof HTMLAnchorElement) {
			campaignCta.textContent = panel.campaign.cta;
			campaignCta.href = panel.campaign.href || "#";
		} else if (campaignCta instanceof HTMLElement) campaignCta.textContent = panel.campaign.cta;
		if (campaignMedia instanceof HTMLElement) {
			if (panel.campaign.image) {
				campaignMedia.style.backgroundImage = "";
				campaignMedia.innerHTML = `<img src="${panel.campaign.image}" alt="" loading="lazy" class="vp-fullscreen-menu__card-image">`;
			} else {
				campaignMedia.style.backgroundImage = "";
				campaignMedia.innerHTML = "";
			}
		}
	};
	if (menuToggle instanceof HTMLButtonElement && fullscreenMenu instanceof HTMLElement) menuToggle.addEventListener("click", () => {
		openFullscreenMenu();
		if (!mobileMenuMq.matches) renderMenuPanel(getDefaultPanelKey());
	});
	if (menuClose instanceof HTMLButtonElement) menuClose.addEventListener("click", closeFullscreenMenu);
	const menuSearchFocus = document.querySelector("[data-vp-menu-search-focus]");
	const menuSearchInput = document.querySelector("[data-vp-menu-search-input]");
	if (menuSearchFocus instanceof HTMLButtonElement && menuSearchInput instanceof HTMLInputElement) menuSearchFocus.addEventListener("click", () => {
		menuSearchInput.focus();
		menuSearchInput.scrollIntoView({
			block: "nearest",
			behavior: "smooth"
		});
	});
	if (fullscreenMenu instanceof HTMLElement) fullscreenMenu.addEventListener("click", (event) => {
		const target = event.target;
		if (!(target instanceof Element)) return;
		const drill = target.closest("[data-vp-menu-drill]");
		if (drill instanceof HTMLElement) {
			const panelKey = drill.getAttribute("data-vp-menu-drill");
			if (panelKey) showMenuPanel(panelKey);
			return;
		}
		const back = target.closest("[data-vp-menu-back]");
		if (back instanceof HTMLElement) {
			const panelKey = back.getAttribute("data-vp-menu-back") || "root";
			showMenuPanel(panelKey);
		}
	});
	menuItems.forEach((item) => {
		if (!(item instanceof HTMLButtonElement)) return;
		item.addEventListener("click", () => {
			if (mobileMenuMq.matches) return;
			const key = item.dataset.vpMenuItem || "BMW";
			menuItems.forEach((btn) => btn.classList.remove("is-active"));
			item.classList.add("is-active");
			if (prefersReducedMotion$1() || !vpGsap || !(menuContentCol instanceof HTMLElement)) {
				renderMenuPanel(key);
				return;
			}
			vpGsap.to(menuContentCol, {
				autoAlpha: 0,
				y: -8,
				duration: .18,
				ease: "power2.in",
				overwrite: true,
				onComplete: () => {
					renderMenuPanel(key);
					vpGsap.fromTo(menuContentCol, {
						autoAlpha: 0,
						y: 12
					}, {
						autoAlpha: 1,
						y: 0,
						duration: .28,
						ease: "power2.out",
						overwrite: true
					});
				}
			});
		});
	});
	document.addEventListener("keydown", (event) => {
		if (event.key !== "Escape") return;
		window.vpHeaderFlyouts?.closeAll();
		if (fullscreenMenu instanceof HTMLElement && !fullscreenMenu.hidden) {
			if (mobileMenuMq.matches) {
				const activePanel = menuPanels.find((panel) => panel.classList.contains("is-active"));
				const activeKey = activePanel?.getAttribute("data-vp-menu-panel");
				if (activeKey && activeKey !== "root") {
					const parentKey = (activePanel?.querySelector("[data-vp-menu-back]"))?.getAttribute("data-vp-menu-back") || "root";
					showMenuPanel(parentKey);
					return;
				}
			}
			closeFullscreenMenu();
			return;
		}
		closeBrandDropdowns();
	});
});
//#endregion
